import { Component, OnInit } from '@angular/core';
import { AlertService } from '../../shared/services/alert.service';
import { BusyService } from '../../shared/services/busy.service';
import { Router, ActivatedRoute } from '@angular/router';
import { StatusEndTimeRulesService } from '../../shared/services/status-end-time-rules.service';
import { LazyLoadEvent, ConfirmationService } from 'primeng/primeng';
import { StatusService } from '../../shared/services/status.service';
import { RegionService } from '../../shared/services/region.service';
import { OrgCategoryService } from '../../shared/services/org-category.service';
import { Observable } from 'rxjs/Observable';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { StatusEndTimeRules } from '../../shared/models/StatusEndTimeRules';
import { AuthService } from '../../shared/services/auth.service';

@Component({
  selector: 'app-time-rule',
  templateUrl: './time-rule.component.html',
  styleUrls: ['./time-rule.component.scss']
})
export class TimeRuleComponent implements OnInit {
  busy = false;
  busyEditDialog = false;
  regionId = 0;
  regionName = '';
  displayEditDialog = false;
  editModel: any = {};
  model = {
    rules: [],
    regions: [],
    categories: [],
    statuses: []
  };
  paginator: any = {
    perPage: 35,
    skip: 0,
    totals: 0
  };
  isAddingNew = false;
  form: FormGroup;
  paginatorCacheKey: string;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private busyService: BusyService,
    private statusEndTimeRulesService: StatusEndTimeRulesService,
    private statusService: StatusService,
    private regionService: RegionService,
    private orgCategoryService: OrgCategoryService,
    private alertService: AlertService,
    private confirmationService: ConfirmationService
  ) {
    const currentUser = AuthService.currentUser();
    if (!currentUser.isInRole(['sysadmin', 'superuser'])) {
      this.router.navigate(['/orgedit/' + currentUser.organizationId]);
    } else {
      this.regionId = currentUser.regionId;
    }

    this.form = new FormGroup({
      region: new FormControl('', [Validators.required]),
      status: new FormControl('', [Validators.required]),
      category: new FormControl('', [Validators.required]),
      endTime: new FormControl('', [Validators.required])
    });

    if (this.regionId > 0) {
      this.form.controls['region'].setValidators([]);
    }

    this.busyService.initHandler('timeRuleCmp', busy => {
      this.busy = busy;
    });

    this.busyService.initHandler('timeRuleEditDlg', busy => {
      this.busy = busy;
    });

    this.paginatorCacheKey =
      'time-rule-paginator-' + currentUser.organizationId.toString();

    this.restoreTableState();
  }

  ngOnInit() {}

  loadData() {
    this.saveTableState();

    this.busyService
      .follow(
        'timeRuleCmp',
        this.statusEndTimeRulesService.count(this.regionId)
      )
      .subscribe(totals => {
        this.paginator.totals = totals;
      });

    this.busyService
      .follow(
        'timeRuleCmp',
        this.statusEndTimeRulesService.get(
          this.regionId,
          this.paginator.skip,
          this.paginator.perPage
        )
      )
      .subscribe(rules => {
        this.model.rules = rules;
      });
  }

  loadLazy($event: LazyLoadEvent) {
    this.paginator.skip = $event.first;
    this.paginator.perPage = $event.rows;
    this.loadData();
  }

  edit(rule) {
    this.busyService.busy('timeRuleEditDlg');
    if (!rule) {
      this.isAddingNew = true;
      this.editModel.regionId = null;
      this.editModel.statusId = null;
      this.editModel.categoryId = null;
      this.editModel.cEndTime = new Date(new Date().setMinutes(0));
      this.readonlyFormsElements(false);
    } else {
      this.editModel = new StatusEndTimeRules(rule);
      this.readonlyFormsElements(true);
    }
    Observable.forkJoin([
      this.regionService.all(),
      this.orgCategoryService.all(),
      this.statusService.all()
    ]).subscribe(result => {
      this.model.regions = result[0];
      if (this.regionId > 0) {
        this.regionName = this.model.regions.filter(
          r => r.regionId === this.regionId
        )[0].regionName;
      }

      this.model.categories = result[1];
      this.model.statuses = result[2];

      if (this.regionId > 0) {
        this.editModel.regionId = this.regionId;
      }
      this.displayEditDialog = true;

      this.busyService.alive('timeRuleEditDlg');
    });
  }

  delete(rule) {
    this.confirmationService.confirm({
      message:
        'Are you sure you would like to delete rule for region  [' +
        rule.regionName +
        '], status [' +
        rule.statusDescription +
        '] and category [' +
        rule.categoryName +
        '] ?',
      accept: () => {
        this.busyService
          .follow(
            'timeRuleCmp',
            this.statusEndTimeRulesService.delete(
              rule.statusId,
              rule.categoryId,
              rule.regionId
            )
          )
          .subscribe(result => {
            this.loadData();
            this.alertService.showSuccess(
              // tslint:disable-next-line:quotemark
              "'Rule' was deleted successfully."
            );
          });
      }
    });
  }

  save() {
    if (this.isAddingNew) {
      if (!this.form.valid) {
        for (const c in this.form.controls) {
          if (!c) {
            continue;
          }
          const control = this.form.controls[c];
          control.markAsDirty();
        }
        return;
      }
    }
    const newItem = new StatusEndTimeRules(this.editModel);

    if (this.editModel.cEndTime instanceof Date) {
      newItem.cEndTime = new Date(this.editModel.cEndTime)
        .toTimeString()
        .slice(0, 5);
      newItem.nEndTime = +newItem.cEndTime.replace(':', '');
    }

    if (this.isAddingNew) {
      this.busyService
        .follow('timeRuleEditDlg', this.statusEndTimeRulesService.add(newItem))
        .subscribe(res => {
          if (res.type === 'warning') {
            this.alertService.showWarning(res.message, 'Warning');
          } else {
            this.alertService.showSuccess(
              // tslint:disable-next-line:quotemark
              "'Rule' was added successfully."
            );
            this.loadData();
            this.displayEditDialog = false;
            this.isAddingNew = false;
            this.form.reset();
          }
        });
    } else {
      this.busyService
        .follow('timeRuleEditDlg', this.statusEndTimeRulesService.save(newItem))
        .subscribe(res => {
          this.alertService.showSuccess(
            // tslint:disable-next-line:quotemark
            "'Rule' was updated successfully."
          );
          this.loadData();
          this.displayEditDialog = false;
          this.isAddingNew = false;
        });
    }
  }

  cancel() {
    this.displayEditDialog = false;
    this.form.reset();
  }

  getToPaginatorLabel() {
    if (this.paginator.perPage > this.paginator.totals) {
      return this.paginator.totals;
    } else {
      if (
        this.paginator.perPage + this.paginator.skip >
        this.paginator.totals
      ) {
        return this.paginator.totals;
      } else {
        return this.paginator.skip + this.paginator.perPage;
      }
    }
  }
  private readonlyFormsElements(readonly) {
    for (const c in this.form.controls) {
      if (!c || c === 'endTime') {
        continue;
      }
      const control = this.form.controls[c];
      if (readonly) {
        control.disable();
      } else {
        control.enable();
      }
    }
  }

  private saveTableState() {
    localStorage.setItem(
      this.paginatorCacheKey,
      JSON.stringify(this.paginator)
    );
  }

  private restoreTableState() {
    if (localStorage.getItem(this.paginatorCacheKey)) {
      this.paginator = JSON.parse(localStorage.getItem(this.paginatorCacheKey));
    }
  }
}



// WEBPACK FOOTER //
// E:/proj/news12/zb-n12/scadmin/client/src/app/authorized/time-rule/time-rule.component.ts